import { createContext, useContext, useMemo, type ReactNode } from 'react'

export const MAX_CALCULATOR_HANDOFF_DAYS = 3660

export type VnTermsSelectionContext = {
  entryId: string
  feature?: string
  icd10: string
  nosology: string
}

export type VnTermsToSickLeaveHandoff = {
  actualDays?: number
  kind: 'vn-terms-to-sick-leave'
  openDateISO?: string
  selection: VnTermsSelectionContext
  target: 'sick-leave'
  version: 1
}

export type SickLeaveToVnTermsHandoff = {
  actualDays?: number
  closeDateISO?: string
  kind: 'sick-leave-to-vn-terms'
  openDateISO?: string
  selection?: VnTermsSelectionContext
  target: 'vn-terms'
  version: 1
}

export type CalculatorHandoff =
  | VnTermsToSickLeaveHandoff
  | SickLeaveToVnTermsHandoff

type CalculatorNavigationContextValue = {
  handoff: CalculatorHandoff | null
  navigateToCalculator: (
    calculatorId: string,
    handoff?: CalculatorHandoff,
  ) => void
}

const CalculatorNavigationContext =
  createContext<CalculatorNavigationContextValue>({
    handoff: null,
    navigateToCalculator: (calculatorId) => {
      if (typeof window !== 'undefined') {
        window.location.hash = `#/calc/${encodeURIComponent(calculatorId)}`
      }
    },
  })

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
): boolean {
  const allowed = new Set(allowedKeys)
  return Object.keys(value).every((key) => allowed.has(key))
}

function isBoundedText(
  value: unknown,
  {
    allowEmpty = false,
    maxLength,
  }: { allowEmpty?: boolean; maxLength: number },
): value is string {
  return (
    typeof value === 'string' &&
    value.length <= maxLength &&
    (allowEmpty || value.length > 0) &&
    value.trim() === value &&
    // eslint-disable-next-line no-control-regex
    !/[\u0000-\u001f\u007f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/.test(
      value,
    )
  )
}

function isValidISODate(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return false
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}

function isValidDays(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value > 0 &&
    value <= MAX_CALCULATOR_HANDOFF_DAYS
  )
}

function hasRepresentableInclusiveEnd(
  startISO: string,
  durationDays: number,
): boolean {
  const start = Date.parse(`${startISO}T00:00:00Z`)
  const end = new Date(start + (durationDays - 1) * 86_400_000)
  return Number.isFinite(end.getTime()) && end.getUTCFullYear() <= 9999
}

function calendarDaysInclusive(
  startISO: string,
  endISO: string,
): number | null {
  const start = Date.parse(`${startISO}T00:00:00Z`)
  const end = Date.parse(`${endISO}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
    return null
  return Math.floor((end - start) / 86_400_000) + 1
}

function isValidSelection(value: unknown): value is VnTermsSelectionContext {
  if (!isPlainObject(value)) return false
  if (!hasOnlyKeys(value, ['entryId', 'feature', 'icd10', 'nosology']))
    return false
  return (
    isBoundedText(value.entryId, { maxLength: 160 }) &&
    isBoundedText(value.icd10, { maxLength: 32 }) &&
    isBoundedText(value.nosology, { maxLength: 500 }) &&
    (value.feature === undefined ||
      isBoundedText(value.feature, { allowEmpty: true, maxLength: 500 }))
  )
}

export function validateCalculatorHandoff(
  value: unknown,
): CalculatorHandoff | null {
  if (!isPlainObject(value) || value.version !== 1) return null

  const actualDays =
    value.actualDays === undefined
      ? undefined
      : isValidDays(value.actualDays)
        ? value.actualDays
        : null
  if (actualDays === null) return null

  const openDateISO =
    value.openDateISO === undefined
      ? undefined
      : isValidISODate(value.openDateISO)
        ? value.openDateISO
        : null
  if (openDateISO === null) return null

  if (
    value.kind === 'vn-terms-to-sick-leave' &&
    value.target === 'sick-leave' &&
    isValidSelection(value.selection) &&
    hasOnlyKeys(value, [
      'actualDays',
      'kind',
      'openDateISO',
      'selection',
      'target',
      'version',
    ]) &&
    (!actualDays ||
      !openDateISO ||
      hasRepresentableInclusiveEnd(openDateISO, actualDays))
  ) {
    return {
      actualDays,
      kind: value.kind,
      openDateISO,
      selection: value.selection,
      target: value.target,
      version: 1,
    }
  }

  const closeDateISO =
    value.closeDateISO === undefined
      ? undefined
      : isValidISODate(value.closeDateISO)
        ? value.closeDateISO
        : null
  if (closeDateISO === null) return null
  if (closeDateISO && !openDateISO) return null
  if (openDateISO && closeDateISO) {
    const rangeDays = calendarDaysInclusive(openDateISO, closeDateISO)
    if (rangeDays === null || (actualDays && actualDays !== rangeDays))
      return null
  }

  if (
    value.kind === 'sick-leave-to-vn-terms' &&
    value.target === 'vn-terms' &&
    (value.selection === undefined || isValidSelection(value.selection)) &&
    hasOnlyKeys(value, [
      'actualDays',
      'closeDateISO',
      'kind',
      'openDateISO',
      'selection',
      'target',
      'version',
    ]) &&
    (!actualDays ||
      !openDateISO ||
      hasRepresentableInclusiveEnd(openDateISO, actualDays))
  ) {
    return {
      actualDays,
      closeDateISO,
      kind: value.kind,
      openDateISO,
      selection: value.selection,
      target: value.target,
      version: 1,
    }
  }

  return null
}

export function CalculatorNavigationProvider({
  calculatorId,
  children,
  handoff,
  onNavigate,
}: {
  calculatorId: string
  children: ReactNode
  handoff?: CalculatorHandoff | null
  onNavigate: (calculatorId: string, handoff?: CalculatorHandoff) => void
}) {
  const validatedHandoff = useMemo(
    () => validateCalculatorHandoff(handoff),
    [handoff],
  )
  const value = useMemo<CalculatorNavigationContextValue>(
    () => ({
      handoff:
        validatedHandoff?.target === calculatorId ? validatedHandoff : null,
      navigateToCalculator: onNavigate,
    }),
    [calculatorId, onNavigate, validatedHandoff],
  )

  return (
    <CalculatorNavigationContext.Provider value={value}>
      {children}
    </CalculatorNavigationContext.Provider>
  )
}

export function useCalculatorNavigation() {
  return useContext(CalculatorNavigationContext)
}
